import { InfoCircle } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ReactNode } from 'react';

interface FieldLabelProps {
    children: ReactNode;
    /** Optional explanatory text tucked behind an "?" icon tooltip. */
    hint?: ReactNode;
    /** Appends a muted "(opcional)" suffix. */
    optional?: boolean;
    htmlFor?: string;
}

/**
 * A chart-config field label with an optional "?" hint icon. The helper prose
 * that used to sit under each field as a muted paragraph now rides a hover
 * tooltip here, keeping the dense config panel scannable.
 */
export default function FieldLabel({ children, hint, optional, htmlFor }: FieldLabelProps) {
    return (
        <div className="mb-2 flex items-center gap-1.5">
            <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
                {children}
                {optional && (
                    <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>
                )}
            </label>
            {hint && (
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label="Mais informações"
                                className="flex size-4 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                            >
                                <InfoCircle className="size-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs whitespace-normal">
                            {hint}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}
